/**
 * Vehicle full-details / history page (/inventory/vehicles/:vin/:stock). Opens from the
 * "View full details" link in the inventory detail modal. Shows one stocking's current
 * identity, an at-a-glance strip, and its change-log timeline over time.
 */
import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { ChangeTypeChip, fieldLabel, formatChangeTime, formatChangeValue } from "../../components/inventory/changeFormat"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import { InventoryChange, VehicleHistory, getVehicleHistory } from "../../api/inventory"

const CHANGE_TYPE_PHRASE: Record<string, string> = {
  arrival: "Arrived — stocked in",
  sold: "Sold",
  unwind: "Unwound — re-stocked",
  transfer: "Transferred",
  cleared: "Removed at source",
}

function describeChange(change: InventoryChange): string {
  if (change.change_type === "field_change") {
    return `${fieldLabel(change.field)}: ${formatChangeValue(change.field, change.old_value)} → ${formatChangeValue(change.field, change.new_value)}`
  }
  if (change.change_type === "cleared") {
    // The vendor dropped this value; we kept the last-known one (sticky enrichment).
    return `${fieldLabel(change.field)} removed at source — kept last known (${formatChangeValue(change.field, change.old_value)})`
  }
  return CHANGE_TYPE_PHRASE[change.change_type] ?? change.change_type
}

export default function VehicleHistoryPage() {
  return (
    <Layout>
      <VehicleHistoryContent />
    </Layout>
  )
}

function VehicleHistoryContent() {
  const { vin, stock } = useParams<{ vin: string; stock: string }>()
  const { dealerGroupId } = useDealerGroup()
  const [data, setData] = useState<VehicleHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (dealerGroupId == null || !vin || !stock) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getVehicleHistory(vin, stock, dealerGroupId)
      .then((result) => { if (!cancelled) setData(result) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load vehicle") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [vin, stock, dealerGroupId])

  if (dealerGroupId == null) {
    return <DealerGroupRequiredNotice surface="vehicle history" />
  }

  return (
    <div style={pageStyle}>
      <Link to="/inventory/vehicles" style={backLinkStyle}>← Back to inventory</Link>
      {loading && <div style={statusStyle}>Loading…</div>}
      {error && <div style={{ ...statusStyle, color: THEME.colors.semantic.danger }}>{error}</div>}
      {data && !loading && <VehicleHistoryBody data={data} />}
    </div>
  )
}

function VehicleHistoryBody({ data }: { data: VehicleHistory }) {
  const { record, changes, other_stockings: otherStockings } = data
  const title = [record.year, record.make, record.model, record.trim].filter(Boolean).join(" ") || "Vehicle"
  const money = (value: number | null) =>
    value == null ? "—" : `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  const num = (value: number | null) => (value == null ? "—" : value.toLocaleString())

  return (
    <>
      <div style={titleStyle}>{title}</div>
      <div style={subtitleStyle}>
        <span style={{ fontFamily: THEME.fonts.mono }}>{record.vin ?? "—"}</span> · Stock{" "}
        {record.stock_number ?? "—"} · {record.rooftop ?? "—"} · {record.tekion_status ?? "—"}
      </div>

      <div style={glanceStyle}>
        <Glance label="Days on lot" value={num(record.age_days)} />
        <Glance label="Times stocked" value={num(record.times_stocked)} />
        <Glance label="Live on site" value={record.website_live == null ? "—" : record.website_live ? "Yes" : "No"} />
        <Glance label="Site photos" value={num(record.website_photos_count)} />
        <Glance label="Website price" value={money(record.website_price)} />
      </div>

      {otherStockings.length > 0 && (
        <div style={crosslinkStyle}>
          This VIN was also stocked as{" "}
          {otherStockings.map((stocking, index) => (
            <React.Fragment key={stocking.stock_number ?? index}>
              {index > 0 && ", "}
              <Link
                to={`/inventory/vehicles/${encodeURIComponent(record.vin ?? "")}/${encodeURIComponent(stocking.stock_number ?? "")}`}
                style={linkStyle}
              >
                {stocking.stock_number ?? "—"}
              </Link>
              {stocking.tekion_status ? ` (${stocking.tekion_status})` : ""}
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={sectionTitleStyle}>
        History{" "}
        <span style={{ fontWeight: 400, color: THEME.colors.neutral.light.textSecondary }}>· newest first</span>
      </div>
      {changes.length === 0 ? (
        <div style={statusStyle}>No changes recorded yet — history builds as you upload daily.</div>
      ) : (
        <div style={timelineStyle}>
          {changes.map((change) => (
            <div key={change.id} style={timelineRowStyle}>
              <div style={timelineTimeStyle}>{formatChangeTime(change.detected_at)}</div>
              <div><ChangeTypeChip type={change.change_type} /></div>
              <div style={timelineDescStyle}>{describeChange(change)}</div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function Glance({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={glanceLabelStyle}>{label}</div>
      <div style={glanceValueStyle}>{value}</div>
    </div>
  )
}

// --- Styles ---

const pageStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
  maxWidth: 920,
}

const backLinkStyle: React.CSSProperties = {
  display: "inline-block",
  marginBottom: THEME.spacing.md,
  fontSize: 13,
  color: THEME.colors.navy,
  textDecoration: "none",
}

const statusStyle: React.CSSProperties = {
  fontSize: 14,
  color: THEME.colors.neutral.light.textSecondary,
  padding: `${THEME.spacing.md} 0`,
}

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: THEME.colors.text,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
}

const glanceStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: THEME.spacing.lg,
  margin: `${THEME.spacing.lg} 0`,
  padding: `${THEME.spacing.md} ${THEME.spacing.lg}`,
  border: `1px solid ${THEME.colors.neutral.light.border}`,
  borderRadius: THEME.borderRadius.md,
}

const glanceLabelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  color: THEME.colors.neutral.light.textSecondary,
}

const glanceValueStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: THEME.colors.text,
  marginTop: 2,
}

const crosslinkStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: THEME.spacing.lg,
}

const linkStyle: React.CSSProperties = {
  color: THEME.colors.navy,
  textDecoration: "none",
  fontFamily: THEME.fonts.mono,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.navy,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  paddingBottom: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
}

const timelineStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const timelineRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "170px 110px 1fr",
  alignItems: "center",
  gap: THEME.spacing.md,
  fontSize: 13,
}

const timelineTimeStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  whiteSpace: "nowrap",
}

const timelineDescStyle: React.CSSProperties = {
  color: THEME.colors.text,
  wordBreak: "break-word",
}
